import {currentUser} from './FirestoreService';
import {ResponseTooLargeError} from './ResponseTooLargeError';

/** @typedef {Array<Record<string, unknown>>} ProcedureResult */

/**
 * Calls a BigQuery stored procedure.
 * @param {string} dataset The dataset that the procedure belongs to.
 * @param {string} procedure The procedure to call.
 * @param {Array<string|undefined>} [procedureArgs] Optional, arguments to pass to the procedure.
 * @param {string} [projectId] Optional, the project ID the dataset belongs to.
 * @return {Promise<ProcedureResult>} A promise that resolves with the result of the procedure.
 */
const callProcedure = async (dataset, procedure, procedureArgs = null, projectId = null) => {
  const requestBody = {dataset, procedure};

  if (procedureArgs) {
    // Wrap arguments in single quotes to treat them as string literals
    requestBody.procedureArgs = procedureArgs;
  }

  if (projectId) {
    requestBody.project = projectId;
  }

  let url = 'https://us-central1-beyondmaps-dev.cloudfunctions.net/bigQueryEndpoint';
  if (process.env.VUE_APP_GCP_ENV === 'production') {
    url = 'https://us-central1-northpoint-production.cloudfunctions.net/bigQueryEndpoint';
  }

  const token = await currentUser()?.getIdToken();
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    body: JSON.stringify(requestBody),
  });

  if (response.status !== 200) {
    const bodyText = await response.text();
    let json;
    try {
      json = JSON.parse(bodyText);
    } catch {
      const message = `bigQueryEndpoint responded ${response.status} with a non-JSON body: ${bodyText.slice(0, 200)}`;
      if (bodyText.includes('Response size was too large.')) {
        throw new ResponseTooLargeError(message);
      }
      throw new Error(message);
    }
    throw new Error(json.error);
  }

  const json = await response.json();
  return json.result;
};

export {
  callProcedure,
};
